/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { Purchases, CustomerInfo } from '@revenuecat/purchases-capacitor';

export const PRO_ENTITLEMENT = 'Doqit Pro';

export const hasProEntitlement = (customerInfo?: CustomerInfo | null): boolean => (
  !!customerInfo?.entitlements?.active?.[PRO_ENTITLEMENT]
);

/**
 * RevenueCat's hosted paywall reports RESTORED once it closes after any
 * restore attempt, including one cancelled at Apple's sign-in prompt or one
 * that found nothing to restore. So RESTORED alone proves nothing: only an
 * active Doqit Pro entitlement counts. Fails closed: if the lookup itself
 * fails this reads as no entitlement, and the re-presented paywall
 * (presentPaywallIfNeeded) runs its own fresh check, so a real subscriber
 * still gets let through by NOT_PRESENTED. Never throws.
 */
export async function restoreGaveEntitlement(): Promise<boolean> {
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return hasProEntitlement(customerInfo);
  } catch (error) {
    console.error('[revenuecat] getCustomerInfo after restore failed', error);
    return false;
  }
}

const toError = (value: unknown): Error => (
  value instanceof Error ? value : new Error(String(value))
);

// Platform detection is wrapped because unit tests mock @capacitor/core with
// only the members the code under test used at the time.
const isAndroid = (): boolean => {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
};

/**
 * Push any Google Play purchase the store knows about up to RevenueCat and
 * return the refreshed CustomerInfo.
 *
 * Why this exists: a Play purchase that completed on the device but never
 * posted to RevenueCat (app killed mid-flow, network drop, receipt post
 * rejected) stays unacknowledged. RevenueCat then reports no "Doqit Pro"
 * entitlement, the hard paywall is presented again, and Play refuses the
 * repeat purchase as a plan change. Nothing in the app ever called
 * syncPurchases(), so that state could only be left by a manual restore.
 *
 * Android only. On iOS syncPurchases can prompt for App Store credentials,
 * and StoreKit already hands RevenueCat every transaction, so it is a no-op
 * there. Never throws: a failure is reported to Sentry and null comes back.
 */
export async function syncAndroidPurchases(context: string): Promise<CustomerInfo | null> {
  if (!isAndroid()) return null;
  try {
    await Purchases.syncPurchases();
    const { customerInfo } = await Purchases.getCustomerInfo();
    return customerInfo ?? null;
  } catch (error) {
    console.error('[revenuecat] syncPurchases failed', error);
    Sentry.captureException(toError(error), {
      extra: { context },
      fingerprint: ['revenuecat-sync-purchases-failed'],
    });
    return null;
  }
}

const isIos = (): boolean => {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  } catch {
    return false;
  }
};

/**
 * iOS counterpart for one specific case: verify or sign-in found no Pro
 * entitlement for someone we have reason to think paid (a guest purchase is
 * pending on this device, or the account carries an anonymous RevenueCat id).
 * The purchase may sit on another install's anonymous customer under the same
 * Apple ID; syncPurchases posts this device's App Store receipt to the
 * account, which attaches it. Only called in that case, once, because on iOS
 * syncPurchases can ask for App Store credentials. Never throws: returns the
 * refreshed CustomerInfo, or null on failure (reported to Sentry).
 */
export async function syncIosPurchasesIfMissing(
  customerInfo: CustomerInfo | null | undefined,
  reasonToExpectPurchase: boolean,
  context: string,
): Promise<CustomerInfo | null> {
  if (!isIos() || !reasonToExpectPurchase || hasProEntitlement(customerInfo)) return customerInfo ?? null;
  try {
    await Purchases.syncPurchases();
    const { customerInfo: refreshed } = await Purchases.getCustomerInfo();
    return refreshed ?? null;
  } catch (error) {
    console.error('[revenuecat] iOS syncPurchases failed', error);
    Sentry.captureException(toError(error), {
      extra: { context },
      fingerprint: ['revenuecat-ios-sync-purchases-failed'],
    });
    return null;
  }
}

/**
 * Purchases.logIn failures used to be swallowed with a console line, so a
 * device whose purchases never attached to the account left no trace. Keep
 * the call sites non-fatal, but make the failure visible with the user id.
 */
export function reportPurchasesLoginFailure(error: unknown, userId: number | string | null | undefined, context: string): void {
  console.error('[revenuecat] logIn failed', { context, userId }, error);
  Sentry.captureException(toError(error), {
    extra: { context, userId: userId ?? null },
    tags: { revenuecat_context: context },
    fingerprint: ['revenuecat-login-failed'],
  });
}

/**
 * Session budget for the hosted paywall's ERROR outcome, shared by
 * showPaywall.ts and NativePaywall.tsx. On Android a purchase that Play
 * rejects inside the paywall (typically "already owned", the plan-change
 * refusal above) surfaces as PAYWALL_RESULT.ERROR once the sheet closes.
 * One re-present is allowed after a sync, which is the chance for the
 * entitlement to appear; after that the session falls through to /pricing,
 * which has Restore purchases, instead of showing the sheet again.
 */
export const MAX_PAYWALL_ERROR_PRESENTATIONS = 2;

let paywallErrorCount = 0;

/** Records one ERROR outcome; true means the paywall may be presented again. */
export function recordPaywallErrorAndCheckBudget(): boolean {
  paywallErrorCount += 1;
  return paywallErrorCount < MAX_PAYWALL_ERROR_PRESENTATIONS;
}

export function paywallErrorBudgetExhausted(): boolean {
  return paywallErrorCount >= MAX_PAYWALL_ERROR_PRESENTATIONS;
}

// eslint-disable-next-line no-underscore-dangle
export const __resetPaywallErrorBudgetForTests = () => {
  paywallErrorCount = 0;
};
